import { useEffect, useRef } from "react";
import { Bot, UserRound, Hand, Check, AlertCircle, MessageCircle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ComposeBox from "@/components/ComposeBox";
import WaText from "@/components/WaText";
import { avatarColor, dayjs, displayPhone, initials } from "@/lib/format";

function Bubble({ msg }) {
  const inbound = msg.direction === "inbound";
  const bot = msg.sender_role === "bot";
  const failed = msg.status === "failed";
  return (
    <div className={`flex ${inbound ? "justify-start" : "justify-end"} animate-msg`} data-testid={`message-${msg.id}`}>
      <div className="min-w-0 max-w-[85%] md:max-w-[65%]">
        <div className={`px-2.5 pt-1.5 pb-1 rounded-lg shadow-[0_1px_0.5px_rgba(11,20,26,.13)] text-[14.2px] leading-[19px] whitespace-pre-wrap [overflow-wrap:anywhere] ${
          inbound ? "bg-white bubble-in" : bot ? "bg-[#E7FCE3] bubble-out" : "bg-[#D9FDD3] bubble-out"}`}>
          {!inbound && (
            <p className={`text-[11.5px] font-bold mb-0.5 flex items-center gap-1 ${bot ? "text-emerald-700" : "text-sky-700"}`}>
              {bot ? <><Bot className="h-3 w-3" />Bot IA</> : <><UserRound className="h-3 w-3" />{msg.sender_name || "Asesor"}</>}
            </p>
          )}
          <WaText text={msg.text} />
          <span className="float-right ml-3 mt-1.5 text-[11px] text-slate-500 flex items-center gap-0.5">
            {dayjs(msg.timestamp).format("HH:mm")}
            {!inbound && (failed ? <AlertCircle className="h-3.5 w-3.5 text-rose-500" /> : <Check className="h-3.5 w-3.5 text-slate-400" />)}
          </span>
        </div>
        {failed && (
          <p className="mt-1 text-[11px] text-rose-600 text-right" data-testid={`message-error-${msg.id}`}>
            No entregado{msg.error ? `: ${msg.error}` : ""}
          </p>
        )}
      </div>
    </div>
  );
}

function DayDivider({ ts }) {
  const d = dayjs(ts);
  const label = d.isSame(dayjs(), "day") ? "Hoy" : d.isSame(dayjs().subtract(1, "day"), "day") ? "Ayer" : d.format("D [de] MMMM");
  return <div className="flex justify-center my-3"><span className="text-[12px] bg-white/90 text-slate-600 rounded-lg px-3 py-1 shadow-sm uppercase">{label}</span></div>;
}

function Empty() {
  return (
    <section data-testid="chat-empty-state" className="flex-1 flex flex-col items-center justify-center bg-[#F7F8FA] border-b-[6px] border-[#25D366] text-center px-6">
      <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center">
        <MessageCircle className="h-9 w-9 text-emerald-600" />
      </div>
      <h3 className="mt-6 font-display text-3xl font-extrabold text-[#41525D]">Tu bandeja de WhatsApp</h3>
      <p className="mt-3 text-sm text-slate-500 max-w-md">
        Elige un chat para ver el historial completo, responder como asesor o dejar que el bot siga atendiendo.
      </p>
      <p className="mt-10 text-xs text-slate-400 flex items-center gap-1.5"><Lock className="h-3 w-3" />Mensajes protegidos con firma de Meta</p>
    </section>
  );
}

export default function ChatFeed({ conversation, messages, onSend, onToggleBot, onClaim, sending, quickReplies }) {
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [messages?.length, conversation?.id]);

  if (!conversation) return <Empty />;
  const c = conversation;

  return (
    <section data-testid="chat-feed" className="flex-1 flex flex-col w-full min-h-0 min-w-0">
      <header data-testid="chat-header" className="h-[60px] shrink-0 bg-[#F0F2F5] border-l border-slate-200 flex items-center px-4 gap-3">
        <div className={`h-10 w-10 rounded-full text-white text-sm font-bold flex items-center justify-center ${avatarColor(c.contact_wa_id)}`}>
          {initials(c.contact_name, c.contact_wa_id)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-[16px] font-semibold text-[#111B21] truncate">{c.contact_name || displayPhone(c.contact_wa_id)}</h2>
            {c.needs_human && (
              <span data-testid="chat-needs-human-badge" className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-bold bg-amber-500 text-white">
                <Hand className="h-2.5 w-2.5" />Necesita humano
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">{displayPhone(c.contact_wa_id)}{c.assigned_agent_name ? ` · Atiende ${c.assigned_agent_name}` : ""}</p>
        </div>
        {c.needs_human && (
          <Button size="sm" data-testid="claim-conversation-button" onClick={onClaim} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
            <UserRound className="h-3.5 w-3.5 mr-1.5" />Tomar chat
          </Button>
        )}
        <label className="flex items-center gap-2 pl-3 ml-1 border-l border-slate-300 cursor-pointer">
          <Bot className={`h-4 w-4 ${c.bot_enabled ? "text-emerald-600" : "text-slate-400"}`} />
          <span className="text-xs font-semibold text-slate-600 hidden md:block" data-testid="bot-state-label">{c.bot_enabled ? "Bot activo" : "Bot pausado"}</span>
          <Switch data-testid="bot-toggle" checked={c.bot_enabled} onCheckedChange={onToggleBot} />
        </label>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin wa-wallpaper">
        <div className="px-[6%] py-4 space-y-1.5">
          {messages.length === 0 && <p className="text-center text-sm text-slate-500 py-10">Sin mensajes todavía.</p>}
          {messages.map((m, i) => (
            <div key={m.id}>
              {(i === 0 || !dayjs(m.timestamp).isSame(messages[i - 1].timestamp, "day")) && <DayDivider ts={m.timestamp} />}
              <Bubble msg={m} />
            </div>
          ))}
        </div>
      </div>

      <ComposeBox disabled={sending} botEnabled={c.bot_enabled} onSend={onSend} quickReplies={quickReplies} channel={c.channel} />
    </section>
  );
}
